import React, { useCallback, useEffect, useMemo } from 'react';

// DEPENDENCIES
import identity from 'lodash/identity';
import isFunction from 'lodash/isFunction';
import startCase from 'lodash/startCase';
import { useNavigate, useLocation } from 'react-router-dom';

// GLOBAL VARIABLES
import { RECORD } from 'pathnames.js';

// GLOBAL FUNCTIONS
import { doCallback, makePath } from 'functions.js';

// GLOBAL HELPERS
import { getRecordData, useRecordContext } from 'helpers/getRecordData';
import usePrevious from 'helpers/usePrevious';
import useRecordView from 'helpers/getRecordView';

// GLOBAL PARTS
import Page from 'parts/page/Page';

// LOCAL PARTS
import RecordButtons from './RecordButtons';

// MAIN COMPONENT
const RecordUpdate = ({
  children,
  title,
  buttons,
  backPathname,
  successPathname,
  saveButton,
  cancelButton,
  deleteButton,
  deleteModal = true,
  initialValues = {},
  formatRecord = identity,
  onLoadRecord,
  onSuccess,
  // REST
  ...props
}) => {

  // REACT ROUTER
  const navigate = useNavigate();
  const { state } = useLocation();

  // CONTEXT
  const { endpoints, record, recordID, recordIDKey, recordLabel } = useRecordContext();
  const { isAdd, isEdit, isGlobal, isNew, isSettings, isView, rootPathname } = useRecordView();

  // MEMOS
  const formattedRecord = useMemo(
    () => doCallback(formatRecord, record),
    [record, formatRecord]
  )
  const initialValuesMemo = useMemo(
    () => isAdd ? initialValues : formattedRecord,
    [isAdd, initialValues,formattedRecord]
  )
  const backPathnameMemo = useMemo(
    () => {
      if (isView || isAdd || isSettings) return makePath(state?.prevPath || backPathname || rootPathname);
      if (isEdit) return makePath(state?.prevPath || makePath(backPathname || rootPathname, RECORD.view, recordID));
    },
    [recordID, isAdd, isEdit, isSettings, isView, rootPathname, backPathname, state]
  );

  const action = useMemo(() => isAdd ? 'Add' : 'Edit', [isAdd]);

  const prefix = useMemo(() => {
    if (isNew !== undefined) {
      if (isNew) {
        return ' New';
      }
      return ' Pre-Owned';
    }
    if (isGlobal !== undefined) {
      if (isGlobal) {
        return 'Global';
      }
      return 'Local';
    }
    return '';
  }, [isNew, isGlobal]);


  // CALLBACKS
  const handleSuccess = useCallback(
    data => {
      doCallback(onSuccess, data);
      const result = data?.result || data;
      const recordID = result[recordIDKey];

      let destination = {};
      if (isFunction(successPathname)) {
        destination = successPathname(result);
      } else {
        const pathname = !!recordID
          ? makePath(successPathname || rootPathname, RECORD.view, recordID)
          : makePath(successPathname || rootPathname);
        destination = { pathname, state: {} };
      }

      navigate(destination?.pathname, { state: destination?.state });
    },
    [recordIDKey, rootPathname, successPathname, onSuccess, navigate]
  )

  // ON LOAD RECORD -- PERHAPS REPLACE THIS WITH REDUX?
  const prevData = usePrevious(record);
  useEffect(() => {
    if (!record) return;
    if (
      !prevData || //
      prevData?.stock_num !== record?.stock_num ||
      prevData?.sales_status_name !== record?.sales_status_name
    ) {
        doCallback(onLoadRecord, record);
      }
  }, [prevData, record, onLoadRecord]);

  // RENDER
  return (
    <Page.Update
      title={title || `${action} ${prefix} ${startCase(recordLabel)}`}
      label={recordLabel}
      method={isEdit ? 'PUT' : 'POST'}
      endpoint={isEdit ? makePath(endpoints.put, recordID) : endpoints.post}
      onSuccess={handleSuccess}
      initialValues={initialValuesMemo}
      backPathname={backPathnameMemo}
      buttons={buttons || (
        <RecordButtons
          backPathname={backPathnameMemo}
          deleteModal={deleteModal}
          saveButton={saveButton}
          cancelButton={cancelButton}
          deleteButton={deleteButton}
        />
      )}
      buttonsAfter={!isView}
      {...props}
    >
      {children}
    </Page.Update>
  );
};

// EXPORT
export default getRecordData(RecordUpdate);
